import { css, html, nothing } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import {
    BIZOPS_EMAIL,
    bizopsMailto,
    TAX_FORM_DOWNLOADS,
    taxFormAction,
    taxFormLabel,
} from '../../lib/tax-form.js';

/**
 * Tells a freelancer which tax form Dice Driven needs from them and how to
 * send it: the blank IRS form to download, and bizops@dicedriven.com to email
 * the completed one to (until it can be uploaded in the app, #249). Shows
 * nothing once the form is on file.
 *
 * Properties:
 *   taxForm  the `tax_form` from GET /api/msa: `{ form, status }`
 */
class TaxFormNotice extends BaseComponent {
    static properties = {
        taxForm: { type: Object, attribute: false },
    };

    static styles = css`
        :host { display: block; }
        .notice {
            border: 1px solid #ffe69c; background: #fff8e1; border-radius: 8px;
            padding: 0.75rem 1rem; font-size: 0.9rem;
        }
        .notice .title { font-weight: 600; margin-bottom: 0.25rem; }
        .notice p { margin-bottom: 0.5rem; }
        .actions { display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem; }
    `;

    render() {
        const action = taxFormAction(this.taxForm);
        if (!action) return nothing;
        const form = this.taxForm.form;
        const name = taxFormLabel(form);
        const download = TAX_FORM_DOWNLOADS[form];
        return html`
            <div class="notice" role="status">
                <div class="title">
                    <i class="bi bi-file-earmark-text"></i>
                    ${action === 'send' ? `Send us your ${name}` : `Send us a new ${name}`}
                </div>
                <p>
                    ${
                        action === 'send'
                            ? `We need your completed, signed ${name} before we can pay you.`
                            : `Your ${name} has expired or is about to, and we need a current one before we can pay you.`
                    }
                    Download the blank form from the IRS, fill it in and sign it, then email it to
                    <a href="${bizopsMailto(form)}">${BIZOPS_EMAIL}</a>. It has your tax ID on it, so send it
                    only to that address.
                </p>
                <div class="actions">
                    <a class="btn btn-outline-primary btn-sm" href="${download.url}" target="_blank" rel="noopener">
                        <i class="bi bi-download"></i> Download the blank ${name}
                    </a>
                    <a href="${download.instructionsUrl}" target="_blank" rel="noopener">IRS instructions</a>
                </div>
            </div>
        `;
    }
}

customElements.define('tax-form-notice', TaxFormNotice);
